import { useCallback, useState } from 'react';
import { organizationApi } from '@/services/organization/organization-api';
import { apiUrlApi } from '@/services/api-sync/sync-api';
import { useAuthStore } from '@/store/useAuthStore';
import { useSignalR } from './useSignalR';
import { setDefaultDepartment } from '@/components/Header/DropDownDepartment';
import { invalidateConfigKeysCache } from '@/services/config/config-keys-store';
import type { License } from '@/types/license';
import {
  clearLicenseInitFlag,
  getLicenseInitParam,
  readLicenseFromStorage,
  saveLicenseToStorage,
} from '@/utils/licenseStorage';
/**
 * Hook xoá cache toàn hệ thống:
 * - Gọi API clearAllCache (BE cũng clear WebSetting)
 * - Fetch lại license (init=1 giống login) rồi ghi đè localStorage
 * - Xoá localStorage keys
 * - Xoá toàn bộ IndexedDB (nếu trình duyệt hỗ trợ)
 */
export const useClearCache = () => {
  const [loading, setLoading] = useState(false);
  const tenantID = useAuthStore((state) => state.tenantID);
  const { sendEventToGroup } = useSignalR();
  const profile = useAuthStore((state) => state.profile);
  const clearIndexedDB = useCallback(async () => {
    if (typeof indexedDB === 'undefined') return;

    try {
      // Trình duyệt hỗ trợ liệt kê databases (Chrome)
      if (typeof (indexedDB as any).databases === 'function') {
        const dbs = await (indexedDB as any).databases();
        await Promise.all(
          (dbs || []).map(
            (db: any) =>
              db?.name &&
              new Promise((resolve) => {
                const req = indexedDB.deleteDatabase(db.name as string);
                req.onsuccess = req.onerror = req.onblocked = () => resolve(true);
              })
          )
        );
      } else {
        // Fallback: thử xoá vài tên DB phổ biến
        const commonDbs = ['localforage', 'bsc-storage', 'action-plan-db'];
        commonDbs.forEach((name) => {
          try {
            indexedDB.deleteDatabase(name);
          } catch {
            // ignore
          }
        });
      }
    } catch {
      // ignore lỗi xoá IDB
    }
  }, []);

  /**
   * Giống login: reset init-flag → getlicense?init=1 (BE FetchLicense + WebSetting mới).
   * Không clear localStorage trước — tránh Navbar thấy license null và nhảy /thiet-lap/license.
   */
  const refreshLicenseAfterClear = useCallback(async (): Promise<License | null> => {
    if (!tenantID || typeof window === 'undefined') {
      return null;
    }

    const previous = readLicenseFromStorage(tenantID) ?? null;
    clearLicenseInitFlag();
    try {
      const initParam = getLicenseInitParam(tenantID); // '1' sau khi clear flag
      const res: any = await apiUrlApi.getLicense(initParam);
      if (!res?.success) return previous;
      const license = (res.data ?? null) as License | null;
      saveLicenseToStorage(tenantID, license);
      return license ?? previous;
    } catch (licenseError) {
      console.error('Failed to refresh license after clear cache:', licenseError);
      return previous;
    }
  }, [tenantID]);

  const clearCache = useCallback(async () => {
    setLoading(true);
    try {
      await organizationApi.clearAllCache();
      const refreshedLicense = await refreshLicenseAfterClear();
      if (typeof window !== 'undefined') {
        // Xoá cache tree select department và position expanded keys
        localStorage.removeItem('department-expanded-tree-paths');
        localStorage.removeItem('position-expanded-keys');
        invalidateConfigKeysCache(tenantID);
      }
      // Xoá toàn bộ IndexedDB
      await clearIndexedDB();
      // Gửi SignalR event để reload indexedDB - gọi trực tiếp từ client
      try {
        await sendEventToGroup(tenantID || '', 'reloadEmployee', {
          title: '',
          body: '',
          type: 'success',
          data: {},
          timestamp: new Date().toISOString(),
        });
      } catch (signalrError) {
        console.error('Failed to send SignalR event:', signalrError);
      }
      // Đồng bộ license mới cho các tab (kèm payload → Navbar không clearCache lại)
      if (refreshedLicense) {
        try {
          await sendEventToGroup(tenantID || '', 'receiveNotification', {
            title: '',
            body: '',
            type: 'reload_license',
            data: { license: refreshedLicense },
            timestamp: new Date().toISOString(),
          });
        } catch (signalrError) {
          console.error('Failed to send reload_license event:', signalrError);
        }
      }
      // Gán lại default bộ phận sau khi clear
      await setDefaultDepartment(profile, tenantID || '');
    } finally {
      setLoading(false);
    }
  }, [clearIndexedDB, tenantID, sendEventToGroup, profile, refreshLicenseAfterClear]);

  return { clearCache, loading };
};
